import React, { useEffect, useState, useContext, useCallback } from 'react'
import moment from 'moment'
import { Form, Col, Modal, Row } from 'react-bootstrap'
import CurrencyFormat from 'react-currency-format'
import { FaEdit } from 'react-icons/fa'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useProgram } from 'hooks/useProgram'
import Loader from 'react-loader-spinner'
import {
  Invalid,
  Permission,
  Pagination,
  LeaveConfirmationButton,
  FormDatePicker,
  StandardSelect,
  InfoTooltip,
  InfoTooltipLink,
  MultiStateCheckbox,
} from 'components'
import parse from 'html-react-parser'
import {
  Toolbar,
  PageButton,
  ModalContainer,
  Header,
  Body,
  TitleName,
  Label,
  Elegibility,
  ExternalLink,
  ProgramUrl,
  SaveButton,
  RegSave,
  DiffTooltip
} from './style'
import { ProgramSchema } from './schema'
import {
  applicationType as appTypes,
  enrollmentStatusList,
} from 'services/options-lists'
import { deserialize, serialize, defaultValues } from './serializer'
import { has, startCase } from 'lodash'
import { useSave as useSaveStagedScholarship } from 'hooks/scholarshipsStage/useSave'
import { useGetOne as useGetOneStagedScholarship } from 'hooks/scholarshipsStage/useGetOne'
import { I18nContext } from 'i18n'
import { DATE_MOMENT_FORMAT, PROGRAM_STATUSES, SCHOLARSHIPS, STAGED_SCHOLARSHIPS } from 'utils/constants'
import uuid from 'uuid/v4'
import classnames from 'classnames'
import { toDate } from 'utils/toDate'
import theme from '../../themes/default'
import { isAbsolute } from 'path'

const { NOT_ACTIVE, NO_LONGER_AVAILABLE } = PROGRAM_STATUSES
const DIFF_NOT_CHANGE = 'No Change'
const openLink = url => window.open(url, '_blank').focus()

const ProgramEditModal = (
  {
    show,
    onHide,
    action = 'edit',
    toolbar,
    items,
    id,
    organization,
    source = SCHOLARSHIPS,
  },
) => {
  const { register, errors, handleSubmit, setValue, formState, reset, control, watch, trigger } = useForm({
    //https://react-hook-form.com/migrate-v5-to-v6 validationSchema
    resolver: yupResolver(ProgramSchema),
    defaultValues,
  })

  const isDirtyAlt = !!Object.keys(formState.dirtyFields).length

  const [canEdit, setCanEdit] = useState(false)

  const { translate } = useContext(I18nContext)

  const watchAllFields = watch()

  const [differences, setDifferences] = useState({})

  const {
    // Create
    createStagedData,
    loadingStagedCreate,
    createStagedProgram,
    // Edit
    editStagedData,
    loadingStagedEdit,
    editStagedProgram,
  } = useSaveStagedScholarship({ editProgramId: id })

  const {
    setUpdateProgramId,
    setProgramId,
    dataDetail,
    programId,
    loadingDetail,
  } = useProgram()
  const [mode, setMode] = useState()

  const {
    data: dataGetOneStaged,
    loading: loadingGetOneStaged,
    reFetch: getOneStaged,
  } = useGetOneStagedScholarship(id, { manual: true })

  useEffect(() => {
    if (mode && dataDetail) {
      reset(deserialize(dataDetail, source))
    }
  }, [mode, dataDetail])

  useEffect(() => {
    if (dataGetOneStaged) {
      reset(deserialize(dataGetOneStaged, source))
      const diff = (dataGetOneStaged?.differences?.data || [])
        .reduce((acc, current) => {
          if (current && current?.status !== DIFF_NOT_CHANGE) {
            acc[current.fieldKey] = current
          }
          return acc
        }, {})
      setDifferences(diff)
    }
  }, [dataGetOneStaged, source])

  useEffect(() => {
    DiffTooltip.rebuild()
  }, [differences])
  const getPropDiff = (prop) => {
    return differences[prop]
  }

  const diffTooltipBool = (prop) => {
    const propDiff = getPropDiff(prop)

    if (propDiff) {
      const uploadedData = has(propDiff, 'uploadedData') ?
        (propDiff.uploadedData ? 'Yes' : 'No')
        : '-'
      const exchangeListing = has(propDiff, 'exchangeListing') ?
        (propDiff.exchangeListing ? 'Yes' : 'No')
        : '-'
      return `Updated to: <span class='text-success'>${uploadedData}</span><br>
        Previous value: <span class='text-danger'>${exchangeListing}</span>`
    }
    return null
  }

  const diffTooltipText = (prop) => {
    const propDiff = getPropDiff(prop)
    if (propDiff) {
      return `Updated to: <span class='text-success'>${propDiff.uploadedData || '-'}</span><br>
        Previous value: <span class='text-danger'>${propDiff.exchangeListing || '-'}</span>`
    }
    return null
  }

  const diffTooltipDate = (prop) => {
    const propDiff = getPropDiff(prop)
    if (propDiff) {
      const uploadDate = propDiff.uploadedData &&
        moment(toDate(propDiff.uploadedData)).format(DATE_MOMENT_FORMAT)
      const exchangeDate = propDiff.exchangeListing &&
        moment(toDate(propDiff.exchangeListing)).format(DATE_MOMENT_FORMAT)
      return `Updated to: <span class='text-success'>${uploadDate || '-'}</span><br>
        Previous value: <span class='text-danger'>${exchangeDate || '-'}</span>`
    }
    return null
  }

  const getSelectStyles = (prop) => {
    const isDiff = getPropDiff(prop)
    const diffColor = theme.palette.success[4]
    return {
      singleValue: (provided, state) => ({
        ...(isDiff && { color: diffColor }),
      }),
      control: (provided, state) => ({
        ...provided,
        ...(isDiff && { borderColor: `${diffColor} !important` }),
        ...(isDiff && { boxShadow: diffColor }),
        "&:hover": {
          ...(isDiff && { borderColor: diffColor }),
        },
      }),
    }
  }

  const getDiffTooltipProps = useCallback((key, { className, dataType } = {}) => {
    return {
      className: classnames(className, {
        'border-success text-success': getPropDiff(key),
      }),
      ...dataType === 'DATE' ? {
        tooltip: { 'data-tip': diffTooltipDate(key), 'data-for': 'diffTooltip' },
      } : {
        'data-tip': diffTooltipText(key),
        'data-for': 'diffTooltip',
      },
    }
  }, [diffTooltipText, getPropDiff, diffTooltipDate])

  const handleShow = useCallback(() => {
    setMode(action)
  }, [setMode, action])

  useEffect(() => {
    setUpdateProgramId(programId)
  }, [programId])

  useEffect(() => {
    if (dataDetail) {
      const { status: { name }, scholarshipStage } = dataDetail
      setCanEdit(name !== NOT_ACTIVE && name !== NO_LONGER_AVAILABLE && !scholarshipStage)
    }
  }, [dataDetail])

  const setBasicFields = useCallback(() => {
    //New program
    setValue('programOrganizationName', organization?.name)
    setValue('programReferenceId', uuid())
  }, [setValue, organization])

  useEffect(() => {
    if (id) {
      ;({
        [SCHOLARSHIPS]: () => setProgramId(id),
        [STAGED_SCHOLARSHIPS]: () => getOneStaged(),
      })[source]()
    } else {
      setBasicFields()
    }
  }, [id])

  useEffect(() => {
    if (createStagedData || editStagedData) {
      onHide(translate('program_edit_staged'))
    }
  }, [createStagedData, editStagedData])

  const onSubmit = (values) => {
    const program = dataDetail || dataGetOneStaged || { organization }
    const data = serialize(values, program, source)
    if (id && source === STAGED_SCHOLARSHIPS) {
      editStagedProgram({ data })
    } else {
      createStagedProgram({ data })
    }
  }

  const handlePageChange = useCallback((item) => {
    const [progr] = item
    setProgramId(progr.id)
  }, [setProgramId])

  const renderToolBar = () => {
    const tool = (
      <Toolbar>
        <Col>
          <Permission permission={['canEditScholarships']}>
            <PageButton
              variant="link"
              onClick={() => setMode('edit')}
              disabled={!canEdit}
            >
              <FaEdit/> EDIT
            </PageButton>
          </Permission>
        </Col>
        <Col>
          {loadingDetail && (
            <Loader type="Oval" color="#9e9e9e" height={20} timeout={20000}/>
          )}
          <div className={classnames({ 'd-none': loadingDetail })}>
            <Pagination
              items={items}
              onPageChange={handlePageChange}
              toolbar
              id={id}
              onClose={onHide}
            />
          </div>
        </Col>
      </Toolbar>
    )
    return mode !== 'edit' ? tool : null
  }

  const renderMessage = (prop) => {
    if (errors[prop]) {
      const { message, type } = errors[prop]
      return type !== 'typeError' ? (
        <Invalid className="edit-mode">{message.replace(prop, '')}</Invalid>
      ) : (
        <Invalid className="edit-mode">Invalid field</Invalid>
      )
    }
    return null
  }

  const renderBooleanStates = ({ text, name, infoStyle, tooltipText }) => (
    <span data-tip={diffTooltipBool(name)} data-for="diffTooltip">
      <Controller
        control={control}
        name={name}
        defaultValue={null}
        render={({ value, onChange }) => (
          <MultiStateCheckbox
            disabled={mode !== 'edit'}
            className={classnames('font-weight-bold', {
              'border-success text-success': getPropDiff(name),
            })}
            {...{ tooltipText, value, onChange, text, name }}
          />
        )}
      />
      {renderMessage(name)}
    </span>
  )

  const renderInput = (
    {
      name,
      required = false,
      readOnly = false,
      display,
      md,
      label,
      tooltip = false,
      className,
      infoStyle,
      isNspaId = false,
      ...rest
    },
  ) => (
    <Row>
      <Form.Group as={Col}>
        <Label 
          className={isNspaId ? "nspaid-label" : "d-flex"} 
          style={{ width: (name === 'numberOfScholarshipsAwarded' && '105%') }}
        >
          {label || startCase(name.replace('scholarship', ''))} {required && '*'}
          {tooltip &&                     
            <InfoTooltipLink 
              text={translate(`program_tooltip_${name}`)} 
              className={isNspaId ? "info-link-icon info-link-icon-lg-title" : "ml-2"}
              placement={isNspaId ? "top" : null}
            />
          }
        </Label>

        <Form.Control
          type="text"
          name={name}
          readOnly={readOnly}
          ref={register}
          className={classnames('edit-mode', className)}
          {...rest}
        />

        <div className={`view-mode ${isNspaId && 'nspaid-view'}`}>{display || watchAllFields?.[name]}</div>

        {renderMessage(name)}
      </Form.Group>
    </Row>
  )

  const renderTitle = useCallback(() => {
    let title = 'view_program'
    if (mode === 'edit') {
      title = id ? 'edit_program' : 'add_program'
    }

    return title
  }, [mode, id])

  const onCancel = useCallback(() => {
    return id && source === SCHOLARSHIPS ? setMode('view') : onHide()
  }, [id, setMode, onHide])

  const handleDateChange = (value) => {
    formState.isSubmitted &&
    setTimeout(async () => {
      await Promise.all([
        trigger('closeDate'),
        trigger('announcementDate')
      ])
    })
    return value
  }

  return (
    <ModalContainer
      show={show}
      onHide={onHide}
      onShow={handleShow}
      size="lg"
      backdrop="static"
    >
      <Header>
        <Modal.Title>
          {translate(renderTitle())}
          <LeaveConfirmationButton
            onClick={onHide}
            requireConfirmation={isDirtyAlt}
            type="Icon"
          />
        </Modal.Title>
      </Header>
      <Body>
        {toolbar && renderToolBar()}
        {mode !== 'edit' && <TitleName>{watchAllFields?.programName}</TitleName>}
        <Form onSubmit={handleSubmit(onSubmit)} className={mode}>
          {mode === 'edit' && <input type="hidden" name="id" ref={register} />}
          <fieldset disabled={loadingStagedCreate || loadingStagedEdit}>
            <Row>
              <Col>
                {/* Organization Name */}
                {renderInput({ name: 'programOrganizationName', readOnly: true, label: 'Organization Name' })}

                {/* Program Name */}
                {
                  renderInput({
                    name: 'programName',
                    required: true,
                    tooltip: true,
                    ...getDiffTooltipProps('programName'),
                  })
                }

                {/* Program Description  */}
                <Row>
                  <Form.Group as={Col}>
                    <Label className="d-flex">
                      Program Description
                      <InfoTooltipLink 
                        text={translate(`program_tooltip_programSelfDescription`)} 
                        className="ml-2"
                      />
                    </Label>

                    <Form.Control
                      type="text"
                      name="programSelfDescription"
                      as="textarea"
                      rows="5"
                      ref={register}
                      {...getDiffTooltipProps('programSelfDescription', { className: 'edit-mode'})}
                    />

                    {
                      watchAllFields?.programSelfDescription && (
                        <div className="view-mode">
                          <Elegibility>{parse(watchAllFields.programSelfDescription)}</Elegibility>
                        </div>
                      )
                    }

                    {renderMessage('programSelfDescription')}
                  </Form.Group>
                </Row>

                {/* Program Website URL */}
                <Row className="mt-2">
                  <Form.Group as={Col}>
                    <Label className="d-flex">
                      Program Website URL
                      {watchAllFields.programURL && (
                        <ExternalLink onClick={() => openLink(watchAllFields.programURL)}/>
                      )}      
                      <InfoTooltipLink
                        text={translate('program_tooltip_programURL')} 
                        className="ml-2"
                      /> 
                    </Label>
                    
                    <Form.Control
                      type="text"
                      name="programURL"
                      autoComplete="off"
                      ref={register}
                      {...getDiffTooltipProps('programURL', { className: 'edit-mode'})}
                    />

                    <ProgramUrl className="view-mode">
                      <a href={watchAllFields.programURL} target="_blank" rel="noopener noreferrer">
                        {watchAllFields.programURL}
                      </a>
                    </ProgramUrl>

                    {renderMessage('programURL')}
                  </Form.Group>
                </Row>
                {/* END Program Website URL */}

                {/* Program Application Link URL */}
                <Row className="mt-2">
                  <Form.Group as={Col}>
                    <Label className="d-flex">
                      Program Application Link URL
                      {watchAllFields.applicationURL && (
                        <ExternalLink
                          onClick={() => openLink(watchAllFields.applicationURL)}
                        />
                      )}
                      <InfoTooltipLink 
                        text={translate('program_tooltip_applicationURL')} 
                        className="ml-2"
                      />
                    </Label>

                    <Form.Control
                      type="text"
                      name="applicationURL"
                      autoComplete="off"
                      ref={register}
                      {...getDiffTooltipProps('applicationURL', { className: 'edit-mode'})}
                    />

                    <ProgramUrl className="view-mode">
                      <a href={watchAllFields.applicationURL} target="_blank" rel="noopener noreferrer">
                        {watchAllFields.applicationURL}
                      </a>
                    </ProgramUrl>

                    {renderMessage('applicationURL')}
                  </Form.Group>
                </Row>
                {/* END Program Application Link URL */}

                {/* Application Type */}
                <Row className="mt-2">
                  <Form.Group as={Col}>
                    <Label className="d-flex">
                      Application Type
                      <InfoTooltipLink 
                        text={translate('program_tooltip_applicationType')} 
                        className="ml-2"
                      />
                    </Label>

                    <span data-tip={diffTooltipText('applicationType')} data-for="diffTooltip">
                      <Controller
                        name="applicationType"
                        control={control}
                        render={({ onChange, value, name, ref }) => (
                          <StandardSelect
                            inputRef={ref}
                            options={appTypes}
                            isSearchable={false}
                            value={appTypes.find(c => c.value === value)}
                            onChange={val => onChange(val?.value)}
                            className="edit-mode"
                            isClearable={true}
                            styles={getSelectStyles('applicationType')}
                          />
                        )}
                      />
                    </span>
                    <div className="view-mode">
                      {watchAllFields?.applicationType}
                    </div>
                    {renderMessage('applicationType')}
                  </Form.Group>
                </Row>
                {/* END Application Type */}

                <Row style={{ width: '140%' }}>
                  {/* Minimum GPA */}
                  <Form.Group as={Col} md={3}>
                    {renderInput({
                      name: 'academicEligibilityMinGPA',
                      md: 12,
                      label: 'Minimum GPA',
                      ...getDiffTooltipProps('academicEligibilityMinGPA'),
                      tooltip: true,
                      infoStyle: { right: '-0.6rem' },
                    })}
                  </Form.Group>
                  {/* Minimum Class Rank */}
                  <Form.Group as={Col} md={3}>
                    {renderInput({
                      name: 'academicEligibilityMinClassRank',
                      md: 12,
                      label: 'Min Class Rank',
                      ...getDiffTooltipProps('academicEligibilityMinClassRank'),
                      tooltip: true,
                      infoStyle: { right: '-0.8rem' },
                    })}
                  </Form.Group>
                  {/* Full/Part Time */}
                  <Form.Group as={Col} md={6}>
                    <Label className="d-flex">
                      Full/Part Time
                      <InfoTooltipLink 
                        text={translate('program_tooltip_enrollmentStatus')} 
                        className="ml-2"
                      />
                    </Label>

                    <span data-tip={diffTooltipText('enrollmentStatus')} data-for="diffTooltip">
                      <Controller
                        name="enrollmentStatus"
                        control={control}
                        render={({ onChange, value, name, ref }) => (
                          <StandardSelect
                            inputRef={ref}
                            options={enrollmentStatusList}
                            isSearchable={false}
                            value={enrollmentStatusList.find(c => c.value === value)}
                            onChange={val => onChange(val?.value)}
                            className="edit-mode"
                            isClearable={true}
                            styles={getSelectStyles('enrollmentStatus')}
                          />
                        )}
                      />
                    </span>
                    <div className="view-mode">
                      {watchAllFields?.enrollmentStatus}
                    </div>
                    {renderMessage('enrollmentStatus')}
                  </Form.Group>
                </Row>

                <Row style={{ width: '140%' }}>
                  {/* Overall SAT */}
                  <Form.Group as={Col} md={3}>
                    {renderInput({
                      name: 'academicEligibilityMinOverallSat',
                      md: 12,
                      label: 'Overall SAT',
                      ...getDiffTooltipProps('academicEligibilityMinOverallSat'),
                      tooltip: true,
                      infoStyle: { right: 0 },
                    })}
                  </Form.Group>
                  {/* Overall ACT */}
                  <Form.Group as={Col} md={3}>
                    {renderInput({
                      name: 'academicEligibilityMinActComposite',
                      md: 12,
                      label: 'Overall ACT',
                      ...getDiffTooltipProps('academicEligibilityMinActComposite'),
                      tooltip: true
                    })}
                  </Form.Group>
                  {/* Maximum AGI */}
                  <Form.Group as={Col} md={3}>
                    {renderInput({
                      name: 'financialEligibilityMaximumAgi',
                      md: 12,
                      label: 'Maximum AGI',
                      ...getDiffTooltipProps('financialEligibilityMaximumAgi'),
                      tooltip: true,
                      infoStyle: { right: '-0.6rem' },
                    })}
                  </Form.Group>
                  {/* Maximum EFC */}
                  <Form.Group as={Col} md={3}>
                    {renderInput({
                      name: 'financialEligibilityMaximumEfc',
                      md: 12,
                      label: 'Maximum EFC',
                      ...getDiffTooltipProps('financialEligibilityMaximumEfc'),
                      tooltip: true,
                      infoStyle: { right: '-0.6rem' },
                    })}
                  </Form.Group>
                </Row>
                      
                {/* Need, Merit & Contest */}
                <Row>
                  <Form.Group as={Col} md={4}>
                    {renderBooleanStates({
                      text: 'Need-Based',
                      name: 'isNeedBased',
                    })}
                  </Form.Group>
                  <Form.Group as={Col} md={4}>
                    {renderBooleanStates({
                      text: 'Merit-Based',
                      name: 'isMeritBased',
                    })}
                  </Form.Group>
                  <Form.Group as={Col} md={4}>
                    {renderBooleanStates({
                      text: 'Contest',
                      name: 'isContest',
                      infoStyle: { right: '-0.6rem' },
                      tooltipText: 'a contest',
                    })}
                  </Form.Group>
                </Row>
                {/* END Need, Merit & Contest */}

              </Col>
              <Col md={3} style={{height: 'fit-content'}}>
                {/* Award Amount */}
                {
                  renderInput({
                    name: 'scholarshipMaximumAward',
                    display: watchAllFields?.scholarshipMaximumAward && <CurrencyFormat
                      value={watchAllFields?.scholarshipMaximumAward}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'$'}
                    />,
                    tooltip: true,
                    label: 'Award Amount',
                    ...getDiffTooltipProps('scholarshipMaximumAward'),
                    infoStyle: { right: '-.9rem' },
                  })
                }

                {/* Minimum Award */}
                {
                  renderInput({
                    name: 'scholarshipMinimumAward',
                    display: watchAllFields?.scholarshipMinimumAward && <CurrencyFormat
                      value={watchAllFields?.scholarshipMinimumAward}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'$'}
                    />,
                    md: 3,
                    tooltip: true,
                    ...getDiffTooltipProps('scholarshipMinimumAward'),
                    infoStyle: { right: '-.9rem' },
                  })
                }

                {/* Number of awards */}
                {renderInput({
                  name: 'numberOfScholarshipsAwarded',
                  md: 12,
                  label: 'Number of Awards',
                  ...getDiffTooltipProps('numberOfScholarshipsAwarded'),
                  tooltip: true,
                  infoStyle: { right: '-.9rem' },
                })}

                {/* Open Date */}
                <Row>
                  <FormDatePicker
                    label={
                        <Label className="d-flex mb-0">
                          Open Date
                          <InfoTooltipLink 
                            text={translate('program_tooltip_openDate')} 
                            className="ml-2"
                          />
                        </Label>
                    }
                    as={Col}
                    name="openDate"
                    display={watchAllFields.openDate}
                    onChange={handleDateChange}
                    {...{
                      control,
                      renderMessage,
                      ...getDiffTooltipProps('openDate', { dataType: 'DATE' }),
                    }}
                  />
                </Row>

                {/* Close Date */}
                <Row>
                  <FormDatePicker
                    label={
                      <Label className="d-flex mb-0">
                        Close Date
                        <InfoTooltipLink 
                          text={translate('program_tooltip_closeDate')} 
                          className="ml-2"
                        />
                      </Label>
                    }
                    as={Col}
                    name="closeDate"
                    display={watchAllFields.closeDate}
                    onChange={handleDateChange}
                    {...{
                      control,
                      renderMessage,
                      ...getDiffTooltipProps('closeDate', { dataType: 'DATE' }),
                    }}
                  />
                </Row>
                {/* END Close Date */}

                {/* Announcement Date */}
                <Row>
                  <FormDatePicker
                    label={
                      <>
                        <Label>Announcement Date</Label>
                        <InfoTooltipLink 
                          text={translate('program_tooltip_announcementDate')} 
                          className="info-link-icon info-link-icon-lg-title mt-1"
                        />
                      </>
                    }
                    as={Col}
                    name="announcementDate"
                    display={watchAllFields.announcementDate}
                    onChange={handleDateChange}
                    {...{
                      control,
                      renderMessage,
                      ...getDiffTooltipProps('announcementDate', { dataType: 'DATE' }),
                    }}
                  />
                </Row>
                {/* END Announcement Date */}
              </Col>
            </Row>

            {mode === 'edit' && (
              <Form.Group>
                <SaveButton
                  variant="primary"
                  type="submit"
                  disabled={loadingStagedCreate || loadingStagedEdit || !isDirtyAlt}
                >
                  {!loadingStagedCreate && !loadingDetail && !loadingGetOneStaged && !loadingStagedEdit ? (
                    <>
                      <RegSave/> <span>SAVE</span>
                    </>
                  ) : (
                    <Loader
                      type="Oval"
                      color="#FFF"
                      height={20}
                      timeout={20000}
                    />
                  )}
                </SaveButton>
                <LeaveConfirmationButton
                  onClick={onCancel}
                  requireConfirmation={isDirtyAlt}
                  text="Cancel"
                />
              </Form.Group>
            )}
            {/* NSPA ID */}
            <hr/>
            {
              renderInput({
                name: 'programReferenceId',
                readOnly: true,
                label: 'NSPA ID',
                tooltip: true,
                infoStyle: { rigth: '0', position: 'absolute'},
                className: 'nspaid-edit',
                isNspaId: true
              })
            }
            {/* END NSPA ID */}
          </fieldset>
        </Form>
      </Body>
      <DiffTooltip id="diffTooltip" place="bottom" html={true} effect="solid" delayShow={50}/>
    </ModalContainer>
  )
}


export default ProgramEditModal
